import React, { useRef, useLayoutEffect, useEffect } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { motion, useAnimation } from 'framer-motion';
import { Reply } from '@styled-icons/fa-solid/Reply';
import { toast } from 'react-toastify';
import { updateContainer } from '../../store/container-actions';
import { extractMentionedUsers } from '../../utils/mentionParser';
import { isUserBlocked, isBlockedBy } from '../../utils/localstorage';
import Message from './Message';
import ReportMessageModal from './ReportMessageModal';
import BlockConfirmationModal from './BlockConfirmationModal';
import ViewProfileModal from './ViewProfileModal';
import ReactionsContainer from './reactions/ReactionsContainer';
import MessageActionsMenu from './MessageActionsMenu';
import HeartAnimation from './reactions/HeartAnimation';
import MessageWithMentions from './MessageWithMentions';

const Container = styled.div`
  width: 100%;
  height: 100%;
`;

const ChatScrollWrapper = styled.div`
  position: absolute;
  top: 4rem;
  bottom: 4rem;
  left: 0;
  right: 0;
  overflow-y: scroll;

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    bottom: calc(4rem + env(safe-area-inset-bottom, 0));
  }
`;

const ChatContainer = styled.div`
  padding: 1rem 1rem 12px 1rem; /* Minimal bottom padding for reactions that peek out */
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  /* Remove min-height: 100% to allow content to exceed container height for scrolling */

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0.5rem 0.5rem 12px 0.5rem; /* Minimal bottom padding for reactions that peek out */
  }
`;

const ChatMessage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  background-color: ${(props) => 
    (props.$isReplyToMe || props.$isMentioningMe)
      ? 'rgba(147, 51, 234, 0.15)' // Purple background for replies to me or mentions of me
      : 'var(--clr-prim)'
  };
  border: ${(props) => 
    (props.$isReplyToMe || props.$isMentioningMe)
      ? '1px solid rgba(147, 51, 234, 0.3)' // Purple border for replies to me or mentions of me
      : 'none'
  };
  border-radius: 12px;
  max-width: 80%;
  align-self: ${(props) => (props.$isSelf ? 'flex-end' : 'flex-start')};
  opacity: ${(props) => props.$fadeLevel || 1};
  transition: opacity 0.3s ease, background-color 0.3s ease, border 0.3s ease;
  word-wrap: break-word;
  overflow-wrap: break-word;
  cursor: ${(props) => (props.$isSelf ? 'default' : 'pointer')};
  margin-bottom: 0; /* Space handled by wrapper div now */

  /* Animation for new messages */
  ${(props) =>
    props.$isNewMessage &&
    `
    animation: slideInMessage 0.4s ease-out;
  `}

  @keyframes slideInMessage {
    0% {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    100% {
      opacity: ${(props) => props.$fadeLevel || 1};
      transform: translateY(0) scale(1);
    }
  }

  &:hover {
    opacity: ${(props) =>
      props.$fadeLevel < 1 ? Math.min(props.$fadeLevel + 0.3, 1) : 1};
  }
`;

const SwipeableMessageWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const ReplyIconContainer = styled.div`
  position: absolute;
  left: ${props => props.$isSelf ? 'auto' : '-40px'};
  right: ${props => props.$isSelf ? '-40px' : 'auto'};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$opacity};
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

const ReplyIcon = styled(Reply)`
  width: 20px;
  height: 20px;
  color: var(--clr-purple);
  transform: ${props => props.$flip ? 'scaleX(-1)' : 'none'};
`;

const MessageHeader = styled.div`
  position: relative;
  width: 100%;
`;

const MessageContent = styled.div`
  flex: 1;
`;

const MessageMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 0.25rem;
  font-size: 12px;
  opacity: 0.7;
  gap: 0.5rem;
`;

const MessageTime = styled.span`
  color: var(--clr-sec);
`;

const MessageNickname = styled.span`
  color: ${(props) => props.$color || 'var(--clr-sec)'};
  font-style: italic;
  font-weight: 300;
`;

const ReplyPreview = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-left: 3px solid ${(props) => props.$replyColor || 'var(--clr-purple)'};
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border-radius: 0 6px 6px 0;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ReplyHeader = styled.div`
  font-size: 11px;
  color: ${(props) => props.$replyColor || 'var(--clr-purple)'};
  font-weight: 500;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ReplyText = styled.div`
  font-size: 12px;
  color: var(--clr-sec);
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;
`;

const NewMessageIndicator = styled.div`
  position: fixed;
  bottom: 6rem;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--clr-purple);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  transform: translateX(-50%)
    translateY(${(props) => (props.$show ? '0' : '10px')});
  pointer-events: ${(props) => (props.$show ? 'auto' : 'none')};

  &:hover {
    background-color: #b060d4;
  }
`;

// Static objects to avoid creating new references on every render
const swipeDragConstraints = { left: 0, right: 80 };
const swipeStyle = { width: '100%', touchAction: 'pan-y' };

// Swipeable message component for handling swipe-to-reply
// IMPORTANT: This must be outside MessagesContainer to avoid re-creating the component on every render
const SwipeableMessage = ({ message, isSelf, fadeLevel, children, onReply }) => {
  const [showReplyIcon, setShowReplyIcon] = useState(false);
  const controls = useAnimation();
  
  return (
    <SwipeableMessageWrapper>
      <ReplyIconContainer 
        $isSelf={isSelf}
        $opacity={showReplyIcon ? 1 : 0}
      >
        <ReplyIcon $flip={!isSelf} />
      </ReplyIconContainer>
      
      <motion.div
        animate={controls}
        drag={isSelf ? false : "x"}
        dragConstraints={swipeDragConstraints}
        dragElastic={0.2}
        dragMomentum={false}
        onDrag={(event, info) => {
          // Show reply icon when swiped more than 30px
          setShowReplyIcon(info.offset.x > 30);
        }}
        onDragEnd={async (event, info) => {
          // Trigger reply if swiped more than 50px
          if (info.offset.x > 50 && !isSelf) {
            onReply(message);
          }
          // Reset state
          setShowReplyIcon(false);
          // Explicitly animate back to original position
          await controls.start({ x: 0, transition: { type: "spring", damping: 40, stiffness: 400 } });
        }}
        style={swipeStyle}
      >
        {children}
      </motion.div>
    </SwipeableMessageWrapper>
  );
};

function MessagesContainer(props) {
  const dispatch = useDispatch();
  const containerRef = useRef();
  const chatContainerRef = useRef();
  const [rect, setRect] = useState(new DOMRect(0, 0, 0, 0));
  const [animatingHeart, setAnimatingHeart] = useState(null);
  const [newMessageIds, setNewMessageIds] = useState(new Set());
  const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [messageToReport, setMessageToReport] = useState(null);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [messageToBlock, setMessageToBlock] = useState(null);
  const [viewProfileModalOpen, setViewProfileModalOpen] = useState(false);
  const [userToView, setUserToView] = useState(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);

  useLayoutEffect(() => {
    if (containerRef && containerRef.current) {
      setRect(containerRef.current.getBoundingClientRect());
    }
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      let rect = containerRef.current.getBoundingClientRect();
      if (
        window.visualViewport &&
        window.innerHeight !== window.visualViewport.height
      ) {
        rect = new DOMRect(
          0,
          window.visualViewport.offsetTop,
          rect.width,
          rect.height -
            (window.innerHeight - window.visualViewport.height) -
            window.visualViewport.offsetTop
        );
      }
      setRect(rect);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      return () => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleResize);
        } else {
          window.removeEventListener('resize', handleResize);
        }
      };
    };
  }, []);

  useEffect(() => {
    dispatch(updateContainer(rect));
  }, [dispatch, rect]);

  const messages = useSelector((state) => Object.values(state.messages.byId));

  const onboardingIndex = useSelector((state) => state.onboarding.index);

  // Track previous message count for new message detection
  const prevMessageCount = useRef(0);
  const wasEmptyBefore = useRef(true);

  // Mark new messages for animation
  useEffect(() => {
    if (props.useChatLayout && messages.length > prevMessageCount.current) {
      const newMessages = messages.slice(prevMessageCount.current);

      if (newMessages.length > 0) {
        setNewMessageIds((prev) => {
          const newSet = new Set(prev);
          newMessages.forEach((msg) => newSet.add(msg.id));
          return newSet;
        });

        // Remove animation flag after animation completes
        setTimeout(() => {
          setNewMessageIds((prev) => {
            const newSet = new Set(prev);
            newMessages.forEach((msg) => newSet.delete(msg.id));
            return newSet;
          });
        }, 500);
      }
    }

    // Track if this is a fresh load (was empty, now has messages)
    wasEmptyBefore.current = prevMessageCount.current === 0;
    prevMessageCount.current = messages.length;
  }, [messages, props.useChatLayout]);

  // Scroll to bottom when forceScrollToBottom prop is true
  useEffect(() => {
    if (props.forceScrollToBottom && props.useChatLayout && chatContainerRef.current) {
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          setShowNewMessageIndicator(false);
          setNewMessageCount(0);
        }
      }, 100);
    }
  }, [props.forceScrollToBottom, props.useChatLayout]);

  // Auto-scroll to bottom when new messages arrive in chat layout, but only if user is already at bottom
  useEffect(() => {
    if (
      props.useChatLayout &&
      chatContainerRef.current &&
      messages.length > 0
    ) {
      const container = chatContainerRef.current;
      // Use a larger threshold to account for long messages
      const threshold = Math.max(200, container.clientHeight * 0.1); // Either 200px or 10% of viewport
      const isAtBottom =
        container.scrollTop >=
        container.scrollHeight - container.clientHeight - threshold;

      if (isAtBottom || wasEmptyBefore.current) {
        // User is at bottom OR this was a fresh load from empty state, auto-scroll and hide indicator
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight;
          }
        }, 0);
        setShowNewMessageIndicator(false);
        setNewMessageCount(0);
      } else {
        // User has scrolled up, increment counter only if indicator is already showing or this is the first message
        if (showNewMessageIndicator) {
          setNewMessageCount((prev) => prev + 1);
        } else {
          setShowNewMessageIndicator(true);
          setNewMessageCount(1);
        }
      }
    }
  }, [messages.length, props.useChatLayout, showNewMessageIndicator]);

  // Hide indicator when user manually scrolls to bottom
  useEffect(() => {
    if (!props.useChatLayout || !chatContainerRef.current) return;

    const container = chatContainerRef.current;
    const handleScroll = () => {
      // Use the same threshold calculation as the auto-scroll logic
      const threshold = Math.max(200, container.clientHeight * 0.1);
      const isAtBottom =
        container.scrollTop >=
        container.scrollHeight - container.clientHeight - threshold;
      if (isAtBottom && showNewMessageIndicator) {
        setShowNewMessageIndicator(false);
        setNewMessageCount(0);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [props.useChatLayout, showNewMessageIndicator]);

  const handleMessageDoubleClick = (e, message) => {
    // Prevent text selection during double-click
    e.preventDefault();
    
    
    // Only allow reactions on other people's messages
    if (message.userId !== 'self' && message.userId !== props.currentUserId) {
      // Check if user already has a heart reaction (check both old and new formats)
      const heartEmoji = 'unicode:❤️';
      const userHasReacted = message.userReactions?.[heartEmoji] === props.currentUserId ||
                            message.userReactions?.['heart'] === props.currentUserId;
      
      if (userHasReacted) {
        // Remove using the format that exists
        if (message.userReactions?.[heartEmoji] === props.currentUserId) {
          props.onRemoveReaction?.(message.id, heartEmoji);
        } else {
          props.onRemoveReaction?.(message.id, 'heart');
        }
      } else {
        // Only show heart animation when adding a reaction
        setAnimatingHeart(message.id);
        props.onAddReaction?.(message.id, heartEmoji);
      }
    }
  };


  const handleBlockUser = (message) => {
    setMessageToBlock(message);
    setBlockModalOpen(true);
  };

  const handleBlockUserDirect = (message) => {
    // Block directly without confirmation modal (used when blocking via report modal checkbox)
    props.sendBlockedUserToServer(message);
  };

  const handleBlockConfirm = (message) => {
    props.sendBlockedUserToServer(message);
    setBlockModalOpen(false);
    setMessageToBlock(null);
  };

  const handleBlockModalClose = () => {
    setBlockModalOpen(false);
    setMessageToBlock(null);
  };

  const handleBanUser = (message) => {
    props.onModBanClick(message);
  };

  const handleWarnUser = (message) => {
    if (props.onModWarnClick) {
      props.onModWarnClick(message);
    }
  };

  const handleFriendRequest = (message) => {
    if (props.onFriendRequestClick) {
      props.onFriendRequestClick(message.userId);
    }
  };

  const handleReport = (message) => {
    setMessageToReport(message);
    setReportModalOpen(true);
  };

  const handleViewProfile = (message) => {
    setUserToView(message.userId);
    setViewProfileModalOpen(true);
  };

  const handleViewProfileModalClose = () => {
    setViewProfileModalOpen(false);
    setUserToView(null);
  };

  const handleReply = (message) => {
    if (props.onReplyClick) {
      props.onReplyClick(message);
    }
  };

  const handleReplyClick = (replyToId) => {
    // Scroll to the original message if it's visible
    const originalMessageWrapper = document.querySelector(`[data-message-id="${replyToId}"]`);
    if (originalMessageWrapper) {
      originalMessageWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Find the ChatMessage component (the rounded message block) within the wrapper
      const chatMessage = originalMessageWrapper.querySelector('[data-chat-message]');
      
      if (chatMessage) {
        // Briefly highlight the message block itself
        const originalBackground = chatMessage.style.backgroundColor;
        chatMessage.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
        chatMessage.style.transition = 'background-color 0.3s ease';
        
        setTimeout(() => {
          chatMessage.style.backgroundColor = originalBackground;
          setTimeout(() => {
            chatMessage.style.transition = '';
          }, 300);
        }, 1500);
      }
    }
  };

  const handleReportModalClose = () => {
    setReportModalOpen(false);
    setMessageToReport(null);
  };

  const handleNewMessageIndicatorClick = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
      // Hide indicator immediately and also trigger the scroll handler
      setShowNewMessageIndicator(false);
      setNewMessageCount(0);
    }
  };


  const renderChatLayout = () => {
    // Single source of truth: just use messages directly
    const chatMessages = messages.slice(-30);

    return (
      <ChatScrollWrapper ref={chatContainerRef}>
        <ChatContainer>
          {chatMessages.map((message, index) => {
            // Only apply fade when we have more than 25 messages, fade oldest 5
            let fadeLevel = 1;
            if (chatMessages.length > 25) {
              if (index < 5) {
                // Fade the first 5 messages (oldest): 0, 1, 2, 3, 4
                fadeLevel = Math.max(0.3, (index + 1) / 5); // Gradual fade from 0.3 to 1.0
              }
            }

            const isSelf = message.userId === 'self' || message.userId === props.currentUserId;
            const isReplyToMe = message.replyToUserId === props.currentUserId || message.replyToUserId === 'self';
            
            // Check if this message mentions the current user
            const mentionedUserIds = extractMentionedUsers(message.messageText || '', messages);
            const isMentioningMe = mentionedUserIds.includes(props.currentUserId) || mentionedUserIds.includes('self');

            return (
              <div 
                key={message.id}
                data-message-id={message.id}
                onMouseEnter={() => setHoveredMessageId(message.id)}
                onMouseLeave={() => setHoveredMessageId(null)}
                style={{ 
                  position: 'relative', 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '8px',
                  flexDirection: isSelf ? 'row-reverse' : 'row',
                  justifyContent: 'flex-start',
                  marginBottom: index === chatMessages.length - 1 ? '0' : '8px', // No margin on last message
                  overflow: 'visible' // Allow reply icon to show outside bounds
                }}
              >
                <SwipeableMessage
                  message={message}
                  isSelf={isSelf}
                  fadeLevel={fadeLevel}
                  onReply={handleReply}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    width: '100%',
                    flexDirection: isSelf ? 'row-reverse' : 'row',
                    pointerEvents: 'none' // Prevent drag on wrapper
                  }}>
                    <ChatMessage
                        $isSelf={isSelf}
                        $fadeLevel={fadeLevel}
                        $isNewMessage={newMessageIds.has(message.id)}
                        $isReplyToMe={isReplyToMe}
                        $isMentioningMe={isMentioningMe}
                        onDoubleClick={(e) => handleMessageDoubleClick(e, message)}
                        data-chat-message
                        style={{ pointerEvents: 'auto' }} // Re-enable pointer events on the actual message
                      >
                  <MessageHeader>
                    <MessageContent>
                      {/* Reply Preview */}
                      {message.replyToId && (() => {
                        const isReplyToBlocked = message.replyToUserId && isUserBlocked(message.replyToUserId);
                        const isReplyToBlocker = !props.isMod && message.replyToUserId && isBlockedBy(message.replyToUserId);
                        const isHidden = isReplyToBlocked || isReplyToBlocker;
                        const hiddenReason = isReplyToBlocked
                          ? 'Hidden because you blocked this user'
                          : 'Hidden because this user blocked you';

                        return (
                          <ReplyPreview
                            $replyColor={isHidden ? '#666' : message.replyToColor}
                            onClick={() => {
                              if (isHidden) {
                                toast.info(isReplyToBlocked
                                  ? 'Original message hidden because you blocked this user'
                                  : 'Original message hidden because this user blocked you');
                              } else {
                                handleReplyClick(message.replyToId);
                              }
                            }}
                            style={isHidden ? { opacity: 0.7, fontStyle: 'italic' } : undefined}
                          >
                            <ReplyHeader $replyColor={isHidden ? '#666' : message.replyToColor}>
                              ↳ {isHidden ? 'Hidden User' : (message.replyToNickname || 'Anon')}
                            </ReplyHeader>
                            <ReplyText>
                              {isHidden ? hiddenReason : message.replyToText}
                            </ReplyText>
                          </ReplyPreview>
                        );
                      })()}

                      <MessageWithMentions
                        messageText={message.messageText}
                        color={message.color}
                        font={message.font}
                        bold={message.bold}
                        onMentionClick={(mentionData) => {
                          // Handle mention click - could show user profile, start DM, etc.
                          console.log('Mentioned user clicked:', mentionData);
                        }}
                      />

                      <MessageMeta>
                        {(message.nickname || message.age || message.pronouns) && (
                          <>
                            <MessageNickname $color={message.color}>
                              {[
                                message.nickname || 'Anon',
                                message.age,
                                message.pronouns
                              ].filter(Boolean).join(' · ')}
                            </MessageNickname>
                            <span>·</span>
                          </>
                        )}
                        <MessageTime>
                          {dayjs(message.createdAt).format('HH:mm')}
                        </MessageTime>
                      </MessageMeta>
                    </MessageContent>
                  </MessageHeader>

                  {/* Reactions - positioned to peek beneath message box */}
                  <ReactionsContainer
                    messageId={message.id}
                    reactions={message.reactions || {}}
                    userReactions={message.userReactions || {}}
                    currentUserId={props.currentUserId}
                    onReactionCountClick={props.onReactionCountClick}
                  />

                  {/* Heart Animation */}
                  <HeartAnimation
                    show={animatingHeart === message.id}
                    onComplete={() => setAnimatingHeart(null)}
                  />
                    </ChatMessage>

                    {/* Message Actions Menu - positioned next to the message */}
                    <div style={{ pointerEvents: 'auto' }}>
                      <MessageActionsMenu
                        message={message}
                        isMod={props.isMod}
                        onFriendRequest={handleFriendRequest}
                        onReport={handleReport}
                        onReply={handleReply}
                        onBlock={handleBlockUser}
                        onBan={handleBanUser}
                        onWarn={handleWarnUser}
                        onViewProfile={handleViewProfile}
                        onAddReaction={props.onAddReaction}
                        onRemoveReaction={props.onRemoveReaction}
                        currentUserId={props.currentUserId}
                        hasSubscription={props.hasSubscription}
                        isHovered={hoveredMessageId === message.id}
                        room={props.room}
                        onBanComplete={props.onModBanComplete}
                      />
                    </div>
                  </div>
                </SwipeableMessage>
              </div>
            );
          })}
        </ChatContainer>
      </ChatScrollWrapper>
    );
  };

  const renderBubbleLayout = () => {
    return (
      <>
        {messages.map((message, index) => (
          <Message
            key={message.id}
            message={message}
            index={index}
            onboardingIndex={onboardingIndex}
            sendBlockedUserToServer={props.sendBlockedUserToServer}
            isMod={props.isMod}
            onModBanClick={props.onModBanClick}
            onFriendRequestClick={props.onFriendRequestClick}
            onReportClick={handleReport}
            onViewProfileClick={props.onViewProfileClick}
          />
        ))}
      </>
    );
  };

  return (
    <Container ref={containerRef}>
      {props.useChatLayout ? renderChatLayout() : renderBubbleLayout()}
      {props.useChatLayout && (
        <NewMessageIndicator
          $show={showNewMessageIndicator}
          onClick={handleNewMessageIndicatorClick}
        >
          {Math.max(0, newMessageCount - 1) === 1
            ? '1 new message'
            : `${Math.max(0, newMessageCount - 1)} new messages`}
        </NewMessageIndicator>
      )}
      <ReportMessageModal
        isOpen={reportModalOpen}
        onClose={handleReportModalClose}
        message={messageToReport}
        room={props.room}
        messageType="room"
        onBlock={handleBlockUserDirect}
      />
      <BlockConfirmationModal
        isOpen={blockModalOpen}
        onClose={handleBlockModalClose}
        onConfirm={handleBlockConfirm}
        message={messageToBlock}
      />
      <ViewProfileModal
        visible={viewProfileModalOpen}
        onClose={handleViewProfileModalClose}
        userId={userToView}
      />
    </Container>
  );
}

export default MessagesContainer;
